import { API_BASE_URL } from '../config/api';
import { clearStoredAuthSession, isAuthSessionRejected } from '../utils/authSessionSync';

// Helper to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Get all conversations for a user
export const getConversations = async (userId, { archived = false } = {}) => {
  try {
    const url = archived
      ? `${API_BASE_URL}/messages/conversations/${userId}?archived=true`
      : `${API_BASE_URL}/messages/conversations/${userId}`;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    let body = null;
    try {
      body = await response.json();
    } catch (_) {
      body = null;
    }
    if (!response.ok) {
      const msg =
        (body && (body.error || body.message)) ||
        (response.status === 401 ? 'Please sign in again' : null) ||
        (response.status === 403 ? (body?.error || 'Access denied') : null) ||
        `Could not load conversations (${response.status})`;
      const err = new Error(msg);
      err.status = response.status;
      throw err;
    }
    return Array.isArray(body) ? body : [];
  } catch (error) {
    console.error('Error fetching conversations:', error);
    if (error.name === 'TypeError' && String(error.message).includes('fetch')) {
      throw new Error('Network error — check that the API server is running and reachable.');
    }
    throw error;
  }
};

// Get messages for a specific conversation
export const getMessages = async (conversationId, userId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}/messages?userId=${userId}`,
      {
        headers: getAuthHeaders()
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch messages');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

// Send a message
// Note: Only Global Enterprise plan can send messages
// Basic and GCC plans cannot message anyone
export const sendMessage = async (conversationId, senderId, content, type = 'text', inquiryProducts = null) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          senderId,
          content,
          type,
          inquiryProducts
        }),
      }
    );
    
    const data = await response.json();
    
    if (!response.ok) {
      // Check if it's a messaging permission error
      if (response.status === 403 && data.errorCode === 'MESSAGING_NOT_ALLOWED') {
        const error = new Error(data.message || 'Messaging is only available with Global Enterprise plan');
        error.code = 'MESSAGING_NOT_ALLOWED';
        error.requiredFeature = 'canLiveChat';
        throw error;
      }
      throw new Error(data.error || 'Failed to send message');
    }
    return data;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Create a new conversation (for inquiries)
// Note: Only Global Enterprise plan can create conversations/message
// Basic and GCC plans cannot message anyone
export const createConversation = async (buyerId, sellerId, type = 'inquiry', inquiryItems = null, initialMessage = null) => {
  try {
    console.log('Creating conversation:', { buyerId, sellerId, type, inquiryItems, initialMessage });
    
    const response = await fetch(`${API_BASE_URL}/messages/conversations`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        buyerId,
        sellerId,
        type,
        inquiryItems,
        initialMessage
      }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Server error:', data);
      if (isAuthSessionRejected(response.status, data)) {
        clearStoredAuthSession({ notify: true });
      }
      // Check if it's a messaging permission error
      if (response.status === 403 && data.errorCode === 'MESSAGING_NOT_ALLOWED') {
        const error = new Error(data.message || 'Messaging is only available with Global Enterprise plan');
        error.code = 'MESSAGING_NOT_ALLOWED';
        error.requiredFeature = 'canLiveChat';
        throw error;
      }
      throw new Error(data.error || 'Failed to create conversation');
    }
    
    return data;
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

// Get unread message count
export const getUnreadCount = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/messages/unread/${userId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch unread count');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching unread count:', error);
    throw error;
  }
};

// Mark conversation as read
export const markAsRead = async (conversationId, userId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}/read`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId })
      }
    );
    if (!response.ok) {
      throw new Error('Failed to mark as read');
    }
    return await response.json();
  } catch (error) {
    console.error('Error marking as read:', error);
    throw error;
  }
};

// Delete conversation
export const deleteConversation = async (conversationId, userId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}?userId=${userId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    if (!response.ok) {
      throw new Error('Failed to delete conversation');
    }
    return await response.json();
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
};

// Send inquiry from product inquiry sidebar
export const sendInquiry = async (buyerId, inquiryItems, message = '') => {
  try {
    console.log('Sending inquiry from buyerId:', buyerId);
    console.log('Inquiry items:', inquiryItems);
    
    // Group items by seller (using sellerId which should be a User ID)
    const itemsBySeller = {};
    inquiryItems.forEach(item => {
      // sellerId should be the user ID of the product owner
      const sellerId = item.sellerId;
      if (!sellerId) {
        console.warn('Item missing sellerId:', item);
        return; // Skip items without sellerId
      }
      if (!itemsBySeller[sellerId]) {
        itemsBySeller[sellerId] = [];
      }
      const savedCurrency = (() => { try { return JSON.parse(localStorage.getItem('selectedCurrency')); } catch { return { symbol: '$', rate: 1 }; } })();
      const sym = savedCurrency?.symbol || '$';
      const rate = savedCurrency?.rate || 1;
      itemsBySeller[sellerId].push({
        id: item.id,
        name: item.description || item.name,
        image: item.image,
        price: `${sym}${(parseFloat(item.priceMin || 0) * rate).toFixed(2)} - ${sym}${(parseFloat(item.priceMax || 0) * rate).toFixed(2)}`,
        unit: item.pricePer || item.unit,
        moq: item.moq,
        description: item.description || item.name
      });
    });

    console.log('Items grouped by seller:', itemsBySeller);

    if (Object.keys(itemsBySeller).length === 0) {
      throw new Error('No valid items to send. Products may be missing seller information.');
    }

    // Create a conversation for each seller
    const results = [];
    for (const [sellerId, items] of Object.entries(itemsBySeller)) {
      console.log(`Creating conversation with seller ${sellerId} for ${items.length} items`);
      const result = await createConversation(
        buyerId,
        parseInt(sellerId),
        'inquiry',
        items,
        message || `Inquiry for ${items.length} product(s)`
      );
      console.log('Conversation created:', result);
      results.push(result);
    }

    return results;
  } catch (error) {
    console.error('Error sending inquiry:', error);
    throw error;
  }
};

// Upload chat attachment (image or file)
export const uploadAttachment = async (conversationId, senderId, file, content = '') => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('senderId', senderId);
    formData.append('content', content);

    const token = getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}/messages-with-attachment`,
      {
        method: 'POST',
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
        body: formData
      }
    );
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 403 && data.errorCode === 'MESSAGING_NOT_ALLOWED') {
        const error = new Error(data.message || 'Messaging not allowed');
        error.code = 'MESSAGING_NOT_ALLOWED';
        throw error;
      }
      throw new Error(data.error || 'Failed to upload attachment');
    }
    return data;
  } catch (error) {
    console.error('Error uploading attachment:', error);
    throw error;
  }
};

// Search messages across conversations
export const searchMessages = async (query) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/search?q=${encodeURIComponent(query)}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Search failed');
    return await response.json();
  } catch (error) {
    console.error('Error searching messages:', error);
    throw error;
  }
};

// Mark messages as read with status update
export const markReadStatus = async (conversationId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}/read-status`,
      { method: 'PATCH', headers: getAuthHeaders() }
    );
    return await response.json();
  } catch (error) {
    console.error('Error marking read status:', error);
    throw error;
  }
};

// Get user profile for chat header (phone, seller slug, etc.)
export const getUserProfile = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/messages/user-profile/${userId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed');
    return await response.json();
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

// Admin: Get chat settings
export const getChatSettings = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/messages/admin/chat-settings`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed');
    return await response.json();
  } catch (error) {
    console.error('Error getting chat settings:', error);
    throw error;
  }
};

// Admin: Update chat settings
export const updateChatSettings = async (settings) => {
  try {
    const response = await fetch(`${API_BASE_URL}/messages/admin/chat-settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(settings)
    });
    if (!response.ok) throw new Error('Failed');
    return await response.json();
  } catch (error) {
    console.error('Error updating chat settings:', error);
    throw error;
  }
};

// Admin: Run cleanup
export const runChatCleanup = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/messages/admin/cleanup`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed');
    return await response.json();
  } catch (error) {
    console.error('Error running cleanup:', error);
    throw error;
  }
};

// Admin: Get all conversations for management
export const getAdminConversations = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/messages/admin/conversations`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch admin conversations');
    return await response.json();
  } catch (error) {
    console.error('Error fetching admin conversations:', error);
    throw error;
  }
};

// Admin: Delete any conversation
export const adminDeleteConversation = async (conversationId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    if (!response.ok) throw new Error('Failed to delete conversation');
    return await response.json();
  } catch (error) {
    console.error('Error deleting conversation (admin):', error);
    throw error;
  }
};

export default {
  getConversations,
  getMessages,
  sendMessage,
  createConversation,
  getUnreadCount,
  markAsRead,
  deleteConversation,
  sendInquiry,
  uploadAttachment,
  searchMessages,
  markReadStatus,
  getUserProfile,
  getChatSettings,
  updateChatSettings,
  runChatCleanup,
  getAdminConversations,
  adminDeleteConversation,
  getContacts,
  createGroupChat
};

// Get contact list for group chat (users from conversations)
export async function getContacts(userId) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/contacts?userId=${userId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed');
    return await response.json();
  } catch (_) {
    return [];
  }
}

// Create a group chat
export async function createGroupChat(memberIds, createdBy) {
  const response = await fetch(`${API_BASE_URL}/messages/groups/create`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ members: memberIds, createdBy })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.message || 'Failed to create group');
    e.code = err.code;
    throw e;
  }
  return response.json();
}

export async function archiveConversation(convId, isArchived) {
  const response = await fetch(`${API_BASE_URL}/messages/conversations/${convId}/archive`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ isArchived })
  });
  if (!response.ok) throw new Error('Failed to update archive status');
  return response.json();
}
